import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import type { UseQueryResult } from "@tanstack/react-query";
import { useCompositionsList } from "@design-lab/entities/composition";
import {
  useMultipleProductPriceData,
  useMultipleProductInventoryData
} from "@design-lab/entities/product";
import { useArtworkForm } from "@design-lab/entities/artwork";
import type { product } from "common/utils/models";

type PriceQueryResult = UseQueryResult<product.cp.Response, Error>;

/**
 * Combines variant prices from multiple query results
 */
function combineVariantPrices(queryResults: PriceQueryResult[]): Record<string, any> {
  return queryResults.reduce((combined, query) => {
    if (!query?.data?.variantPrices) return combined;
    return { ...combined, ...query.data.variantPrices };
  }, {});
}

/**
 * Combines minimum prices for colors from multiple query results
 */
function combineMinPricesForColors(queryResults: PriceQueryResult[]): Record<string, any> {
  return queryResults.reduce((combined, query) => {
    if (!query?.data?.minPricesForColors) return combined;
    return { ...combined, ...query.data.minPricesForColors };
  }, {});
}

/**
 * Hook for getting combined price and inventory data for all products in the form
 */
export function useCombinedProductData() {
  // Get composition products from useCompositionsList
  const { products } = useCompositionsList();
  const { form } = useArtworkForm();

  // Watch artwork form data to determine print mode
  const artworkForm = useWatch({ control: form.control });

  // Determine print mode based on placements
  const printMode = useMemo(() => {
    const hasFrontArtwork = artworkForm?.front?.artworkId || artworkForm?.frontLeftChest?.artworkId;
    const hasBackArtwork = artworkForm?.back?.artworkId;

    // If we have both front and back placements, use print_front_back
    return hasFrontArtwork && hasBackArtwork ? "print_front_back" : "print_default";
  }, [artworkForm]);

  // Extract product IDs directly from products data
  const productIds = useMemo(
    () => products.map(productData => productData.data?.id).filter(Boolean) as string[],
    [products]
  );

  // Use the multi-product hooks with the determined print mode
  const priceQueries = useMultipleProductPriceData(productIds, printMode);
  const inventoryQueries = useMultipleProductInventoryData(productIds);

  const combinedPriceData = useMemo(() => {
    if (!priceQueries.length) {
      return { variantPrices: {}, minPricesForColors: {} } as product.cp.Response;
    }

    const result: Partial<product.cp.Response> = {
      variantPrices: combineVariantPrices(priceQueries),
      minPricesForColors: combineMinPricesForColors(priceQueries)
    };

    return result as product.cp.Response;
  }, [priceQueries]);

  const combinedInventoryData = useMemo(
    () =>
      inventoryQueries.reduce((result, query) => {
        if (query?.data) {
          return { ...result, ...query.data };
        }
        return result;
      }, {}),
    [inventoryQueries]
  );

  const isLoading =
    products.some(p => p.isLoading) ||
    (productIds.length > 0 &&
      (priceQueries.some(q => q.isLoading) || inventoryQueries.some(q => q.isLoading)));

  const isInventoryLoading = inventoryQueries.some(q => q.isLoading);

  const hasError =
    products.some(p => p.isError) ||
    (productIds.length > 0 &&
      (priceQueries.some(q => q.isError) || inventoryQueries.some(q => q.isError)));

  return {
    priceData: combinedPriceData,
    inventoryData: combinedInventoryData,
    isLoading,
    hasError,
    priceQueries,
    inventoryQueries,
    isInventoryLoading
  };
}
